// Update warnings (DR-0098; Theo, 13 Oct 2026: "maybe some kind of like red warning we can push across all effected
// games saying "UPDATE INCOMING, SERVER RESET""). A slim red bar across the top of the page, over everything and never
// in the way of input: the notice's words and a countdown to the reset, pulsing in the last minute, then SERVER
// RESETTING until the home server answers as a new process, and the game regions' servers too (masta controlla, 13 Oct
// 2026: SEA came back 3 minutes after US, its matches still to end), or 5 minutes after the home server is back. It
// asks the home server (GET /notice) every 20 s while the page is visible, and at once when the page comes back; while
// a notice is up, the regions' servers too.
//
// While it shows, `--act-server-notice-h` on :root is its height under the safe area (30 px, 24 on phones), so a
// page's top-pinned buttons step down by `var(--act-server-notice-h, 0px)` rather than sit unseen under it (client-
// core's corner buttons, party bar and toasts do; Review Firestrike asked, 13 Oct 2026).
//
// Import-light on purpose (DOM only, its own styles), so Ludus and Vector Drift mount it without the client-core
// barrel:
//   import { startServerNotice } from "@act/client-core/notice";
//   startServerNotice({ endpoint: resolveColyseusEndpoint() });
import type { ServerNoticeReply } from "@act/shared";
import { availableGameRegions } from "../network/gameRegions";
import { formatCountdown, NoticeTracker, type NoticeView } from "./noticeView";

const POLL_MS = 20_000;
const TIMEOUT_MS = 5_000;
const RESETTING_TEXT = "SERVER RESETTING";
const STYLE_ID = "act-server-notice-style";
const WARNING_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linejoin="round"><path d="M12 3 2 21h20L12 3z"/><path d="M12 10v5M12 18v.5" stroke-linecap="round"/></svg>';
const STYLES = `
:root:has(.act-server-notice:not([hidden])) { --act-server-notice-h: 30px; }
.act-server-notice {
  position: fixed; top: 0; left: 0; right: 0; z-index: 2147483000;
  display: flex; align-items: center; justify-content: center; gap: 10px;
  box-sizing: border-box; min-height: calc(30px + env(safe-area-inset-top, 0px));
  padding: env(safe-area-inset-top, 0px) 12px 0;
  background: #c4141c; color: #fff; box-shadow: 0 2px 14px rgba(0, 0, 0, 0.45);
  font: 700 13px/1 Saira, "Helvetica Neue", Arial, sans-serif; letter-spacing: 0.16em; text-transform: uppercase;
  white-space: nowrap; pointer-events: none; user-select: none;
}
.act-server-notice[hidden] { display: none; }
.act-server-notice__icon svg { display: block; width: 16px; height: 16px; }
.act-server-notice__time { padding-left: 10px; border-left: 1px solid rgba(255, 255, 255, 0.5); font-variant-numeric: tabular-nums; }
.act-server-notice__time:empty { display: none; }
.act-server-notice.is-urgent { animation: act-server-notice-pulse 1s ease-in-out infinite; }
@keyframes act-server-notice-pulse { 50% { background: #ff2b2b; } }
@media (prefers-reduced-motion: reduce) { .act-server-notice.is-urgent { animation: none; background: #e3191f; } }
@media (max-width: 600px), (max-height: 500px) {
  :root:has(.act-server-notice:not([hidden])) { --act-server-notice-h: 24px; }
  .act-server-notice { gap: 7px; min-height: calc(24px + env(safe-area-inset-top, 0px)); font-size: 11px; letter-spacing: 0.1em; }
  .act-server-notice__icon svg { width: 13px; height: 13px; }
  .act-server-notice__time { padding-left: 7px; }
}`;

export type ServerNoticeOptions = {
  /** The home game server (http/https), e.g. resolveColyseusEndpoint(). */
  endpoint: string;
  /** The other servers a reset takes down, whose return the bar waits for: the game regions' unless given. */
  others?: readonly string[];
  /** Where the bar goes: document.body, unless the page goes full screen on an element of its own (Vector Drift's
   *  #app), where it must sit inside it to stay seen. */
  mount?: HTMLElement;
};

/** Watches the home server for notices and shows them; returns a function that stops it and takes the bar away. */
export function startServerNotice(options: ServerNoticeOptions): () => void {
  const home = options.endpoint.replace(/\/+$/, "");
  const others = [...new Set((options.others ?? availableGameRegions().map((region) => region.endpoint)).map((e) => e.replace(/\/+$/, "")))].filter(
    (endpoint) => endpoint !== "" && endpoint !== home,
  );
  const tracker = new NoticeTracker();
  const bar = makeBar();
  (options.mount ?? document.body).appendChild(bar.root);
  let stopped = false;
  const render = (): void => bar.show(tracker.view(Date.now()));
  const ask = async (server: string): Promise<ServerNoticeReply | null> => {
    try {
      const response = await fetch(`${server}/notice`, { cache: "no-store", signal: AbortSignal.timeout(TIMEOUT_MS) });
      return response.ok ? ((await response.json()) as ServerNoticeReply) : null;
    } catch {
      return null; // offline, or the server mid-restart: keep what we knew
    }
  };
  const poll = async (): Promise<void> => {
    if (stopped || document.visibilityState !== "visible") return;
    const reply = await ask(home);
    if (reply) tracker.apply(reply, Date.now());
    if (tracker.watching()) {
      await Promise.all(
        others.map(async (server) => {
          const other = await ask(server);
          if (other) tracker.applyOther(server, other.bootId);
        }),
      );
    }
    if (!stopped) render();
  };
  const onVisible = (): void => {
    if (document.visibilityState === "visible") void poll();
  };
  const poller = window.setInterval(() => void poll(), POLL_MS);
  const ticker = window.setInterval(render, 1000);
  document.addEventListener("visibilitychange", onVisible);
  void poll();
  return () => {
    stopped = true;
    window.clearInterval(poller);
    window.clearInterval(ticker);
    document.removeEventListener("visibilitychange", onVisible);
    bar.root.remove();
  };
}

function makeBar(): { root: HTMLElement; show: (view: NoticeView) => void } {
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = STYLES;
    document.head.appendChild(style);
  }
  const root = document.createElement("div");
  root.className = "act-server-notice";
  root.setAttribute("role", "status");
  root.hidden = true;
  const icon = document.createElement("span");
  icon.className = "act-server-notice__icon";
  icon.setAttribute("aria-hidden", "true");
  icon.innerHTML = WARNING_ICON;
  const text = document.createElement("span");
  const time = document.createElement("span");
  time.className = "act-server-notice__time";
  time.setAttribute("aria-hidden", "true"); // read out with the words once, not every second
  root.append(icon, text, time);
  let shown = "";
  return {
    root,
    show(view) {
      const words = view.mode === "countdown" ? view.text : view.mode === "resetting" ? RESETTING_TEXT : "";
      const clock = view.mode === "countdown" ? formatCountdown(view.remainingMs) : "";
      const urgent = view.mode === "resetting" || (view.mode === "countdown" && view.urgent);
      const key = `${words}|${clock}|${urgent}`;
      if (key === shown) return;
      shown = key;
      root.hidden = view.mode === "hidden";
      if (text.textContent !== words) text.textContent = words;
      time.textContent = clock;
      root.classList.toggle("is-urgent", urgent);
    },
  };
}
